// Auto-matching: what the machine would take, and what it actually did.
//
// The rule this screen serves is not "never act" — it is that nothing is
// disposed of without being attributable and reversible. So the flow is
// preview -> review -> confirm, and the review happens BEFORE the money moves:
// whether the resulting matches then wait for a reviewer follows the client's
// `manual_match_policy`, and Ruby Hall is on `immediate`, so a review offered
// afterwards would never be a gate for the client using it.
//
// THE THING THIS MODULE EXISTS TO GET RIGHT. Measured on run 41: 2,367 matches
// disposed of 4,732 entries worth Rs 6.15 crore — and moved `open_exposure` by
// Rs 0.00, closing 2 parties of 249. That is not a bug. Exposure is per-party
// |sum(26AS) - sum(books)| over ALL of that party's rows, so matching entries
// INSIDE a party changes neither sum. Every figure this screen states is
// therefore at ENTRY grain, and nothing here promises the gap will narrow. A
// user who presses Auto-match and sees the headline unchanged must be able to
// read what did happen; otherwise the feature looks broken, and on the numbers
// above they would be right to think so.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, postJson } from './client'

export interface AutomatchOffer {
  fingerprints: string[]
  kind: string
  band: string | null
  cardinality: string
  tds: string
  residual: string
  party?: string
  members_26as: unknown[]
  members_books: unknown[]
  /** on sampled rows only: which edge of the batch this one is */
  why?: string
}

export interface AutomatchBatch {
  rule: string
  /** the rule as a sentence, built from the figures actually in force */
  sentence: string
  n: number
  tds: string
  parties: number
  sample: AutomatchOffer[]
  sample_covers_all: boolean
  offers: AutomatchOffer[]
}

export interface AutomatchPlan {
  batches: AutomatchBatch[]
  /** why offers were left for a person, by reason */
  skipped: Record<string, number>
  /** the party sweep stopped short of a full answer */
  limited: boolean
  parties_searched: number
  narrow_search: boolean
  dup_review_needed: number
  ambiguous: number
}

export interface AutomatchRefusal {
  fingerprints: string[]
  party?: string | null
  reason: string
}

export interface AutomatchResult {
  batch_id: string
  rules: string[]
  accepted: number
  by_rule: Record<string, number>
  refused: AutomatchRefusal[]
  pending_review: boolean
}

/** Ticked when the panel opens — EVERY rule (nine-point round N3, the
 *  user's explicit decision reversing the old strong-off default): the
 *  machine matches everything and the reconciler REVIEWS instead of
 *  clicking through accepts. Weak matches are taken with a visible
 *  "matched on amount alone — check" flag and stay findable on the
 *  worklist; every match is labelled, reversible, and not carried
 *  forward. The aggregate rung is deliberately NOT a rule — it ties only
 *  because nothing else claimed it. Unticking a rule here is the
 *  per-batch leave-out.
 */
export const DEFAULT_RULES = [
  'exact', 'within tolerance', 'strong', 'medium', 'weak',
]

const group = (n: number) => n.toLocaleString('en-IN')

export interface AutomatchSummary {
  offers: number
  /** entries, which is what every other progress figure on the run counts */
  entries: number
  tds: string
  parties: number
  /** the search stopped short, so these counts are a floor rather than a total */
  isFloor: boolean
}

/** What the ticked rules would take, in the units the rest of the run uses. */
export function automatchSummary(plan: AutomatchPlan,
                                 rules: string[]): AutomatchSummary {
  const chosen = plan.batches.filter((b) => rules.includes(b.rule))
  let offers = 0
  let entries = 0
  let paise = 0
  const parties = new Set<string>()
  for (const b of chosen) {
    for (const o of b.offers) {
      offers += 1
      entries += o.fingerprints.length
      if (o.party) parties.add(o.party)
    }
    paise += Math.round(Number(b.tds) * 100)
  }
  const whole = Math.floor(Math.abs(paise) / 100)
  const frac = Math.abs(paise) % 100
  return {
    offers,
    entries,
    tds: frac ? `${paise < 0 ? '-' : ''}${whole}.${String(frac).padStart(2, '0')}`
      : `${paise < 0 ? '-' : ''}${whole}`,
    parties: parties.size,
    isFloor: plan.limited,
  }
}

/** Why there is nothing to take — never silence, and never "nothing ties".
 *
 *  "We did not look" and "nothing ties" are different answers to a reconciler,
 *  and the difference is usually a setting rather than the data. Returns null
 *  when there IS something takeable.
 */
export function whyNothing(plan: AutomatchPlan): string | null {
  if (plan.batches.length > 0) return null
  const left = Object.entries(plan.skipped).sort((a, b) => b[1] - a[1])
  const bits: string[] = []
  if (!plan.narrow_search) {
    bits.push(
      'The narrowed search is not enabled for this client, so combinations are '
      + 'never banded and the Strong rule can take nothing — the search did not '
      + 'look, which is not the same as nothing tying.',
    )
  }
  if (left.length) {
    bits.push(
      'Left for a person: '
      + left.map(([why, n]) => `${group(n)} ${why}`).join(' · ') + '.',
    )
  }
  if (!bits.length) {
    bits.push('Nothing in this run meets any of the rules the machine may act on.')
  }
  return bits.join(' ')
}

/** What actually happened, stated in entries and parties.
 *
 *  Deliberately says nothing about exposure, the gap, or parties reconciled:
 *  on the run this was measured against, 2,367 matches moved exposure by zero.
 */
export function outcomeLine(result: AutomatchResult, entries: number,
                            parties: number): string {
  const bits = [
    `Matched ${group(entries)} ${entries === 1 ? 'entry' : 'entries'} `
    + `across ${group(parties)} ${parties === 1 ? 'company' : 'companies'}, `
    + `in ${group(result.accepted)} ${result.accepted === 1 ? 'group' : 'groups'}.`,
  ]
  if (result.refused.length) {
    bits.push(`${group(result.refused.length)} refused and left open.`)
  }
  if (result.pending_review) {
    bits.push('These await a reviewer before they count.')
  }
  bits.push('Un-match any of them in one click.')
  return bits.join(' ')
}

export function useAutomatchPreview(runId: string | undefined, open: boolean) {
  return useQuery({
    queryKey: ['automatch-preview', runId],
    queryFn: () => api<AutomatchPlan>(`/runs/${runId}/difference/automatch/preview`),
    // the sweep is ~20s on a real engagement, so it runs when the panel is
    // opened rather than on every visit to the page
    enabled: !!runId && open,
    staleTime: 60_000,
  })
}

export function useAutomatch(runId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    // postJson, NOT a hand-rolled fetch init: without an explicit
    // Content-Type, fetch stamps the body text/plain and FastAPI 422s it
    // before reading a byte. This was the ONE mutation in the codebase built
    // on raw api() with a JSON body, and it failed on its first real press —
    // the visual pass had verified the preview but deliberately never pressed
    // Match, so this line had never once run against a server.
    mutationFn: (rules: string[]) =>
      postJson<AutomatchResult>(`/runs/${runId}/difference/automatch`, { rules }),
    onSuccess: () => {
      // a match changes the grid, the company panels, the cleared view, the
      // worklist and the progress meters — and the preview it came from
      for (const key of ['automatch-preview', 'difference', 'grid', 'companies',
        'match-suggestions', 'cleared', 'reconciliation', 'resolutions',
        'resolution-progress', 'parties']) {
        void qc.invalidateQueries({ queryKey: [key] })
      }
    },
  })
}
